import React, { useRef, useState, useEffect } from "react";
import "./WhiteboardSpeedPaint.css";

export default function WhiteboardSpeedPaint() {
  const canvasRef = useRef(null);
  const hiddenCanvasRef = useRef(null);
  const handCanvasRef = useRef(null);
  const finalCanvasRef = useRef(null);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const [file, setFile] = useState(null);
  const [image, setImage] = useState(null);

  const [brushSize, setBrushSize] = useState(16);
  const [speed] = useState(3); // Balanced speed
  const [progress, setProgress] = useState(0);
  const [handEnabled] = useState(true);

  const handImg = useRef(null);
  const handPos = useRef({ x: 0, y: 0 });
  const handTarget = useRef({ x: 0, y: 0 });

  // Load hand image
  useEffect(() => {
    const img = new Image();
    img.src = "/hand.png";
    handImg.current = img;
  }, []);

  // Load image and prepare all canvases
  useEffect(() => {
    if (!file) return;

    const img = new Image();
    img.onload = () => {
      setImage(img);

      const canvas = canvasRef.current;
      const handCanvas = handCanvasRef.current;
      const finalCanvas = finalCanvasRef.current;
      const hidden = (hiddenCanvasRef.current = document.createElement("canvas"));

      canvas.width = hidden.width = handCanvas.width = finalCanvas.width = img.width;
      canvas.height =
        hidden.height =
        handCanvas.height =
        finalCanvas.height =
          img.height;

      const hctx = hidden.getContext("2d");
      hctx.drawImage(img, 0, 0);

      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Auto-start whole animation
      setTimeout(() => {
        startRecording();
        startPainting();
      }, 600);
    };

    img.src = URL.createObjectURL(file);
  }, [file]);

  // Smooth cinematic hand animation
  useEffect(() => {
    let frame;
    const handCanvas = handCanvasRef.current;
    const hctx = handCanvas?.getContext("2d");

    const animate = () => {
      if (!image) return (frame = requestAnimationFrame(animate));

      // Smooth hand movement ease
      handPos.current.x += (handTarget.current.x - handPos.current.x) * 0.15;
      handPos.current.y += (handTarget.current.y - handPos.current.y) * 0.15;

      hctx.clearRect(0, 0, handCanvas.width, handCanvas.height);

      if (handEnabled && handImg.current) {
        hctx.drawImage(handImg.current, handPos.current.x, handPos.current.y, 90, 90);
      }

      frame = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(frame);
  }, [image, handEnabled]);

  // Merge draw+hand into final canvas
  const mergeCanvases = () => {
    const finalCanvas = finalCanvasRef.current;
    const fctx = finalCanvas.getContext("2d");

    fctx.clearRect(0, 0, finalCanvas.width, finalCanvas.height);
    fctx.drawImage(canvasRef.current, 0, 0);
    fctx.drawImage(handCanvasRef.current, 0, 0);
  };

  // MAIN drawing engine (automatic)
  const startPainting = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const source = hiddenCanvasRef.current;

    const width = canvas.width;
    const height = canvas.height;
    const totalPixels = width * height;

    const pixelSteps = Math.max(2000 / speed, 500);
    const stepCount = Math.floor(totalPixels / pixelSteps);

    let index = 0;

    const interval = setInterval(() => {
      for (let i = 0; i < stepCount && index < totalPixels; i++) {
        const x = index % width;
        const y = Math.floor(index / width);

        ctx.drawImage(source, x, y, 1, 1, x, y, brushSize, brushSize);

        handTarget.current.x = x;
        handTarget.current.y = y;

        index++;
      }

      // Progress bar
      setProgress(Math.min(100, Math.floor((index / totalPixels) * 100)));

      mergeCanvases();

      if (index >= totalPixels) {
        clearInterval(interval);
        setTimeout(() => stopRecording(), 1000);
      }
    }, 10);
  };

  // Start recording
  const startRecording = () => {
    const finalCanvas = finalCanvasRef.current;
    const stream = finalCanvas.captureStream(30);

    recorderRef.current = new MediaRecorder(stream, { mimeType: "video/webm" });
    chunksRef.current = [];

    recorderRef.current.ondataavailable = (e) => chunksRef.current.push(e.data);

    recorderRef.current.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      const url = URL.createObjectURL(blob);

      // Auto download
      const a = document.createElement("a");
      a.href = url;
      a.download = "speedpaint_balanced.webm";
      a.click();

      URL.revokeObjectURL(url);
    };

    recorderRef.current.start();
  };

  const stopRecording = () => recorderRef.current?.stop();

  return (
    <div className="wbp-container">
      <h1 className="wbp-title">🎨 Auto Whiteboard Speed Paint</h1>

      <input
        type="file"
        accept="image/*"
        className="wbp-upload"
        onChange={(e) => setFile(e.target.files[0])}
      />

      {/* Progress Bar */}
      {progress > 0 && progress < 100 && (
        <div className="wbp-progress-wrapper">
          <div className="wbp-progress-bar" style={{ width: `${progress}%` }}></div>
          <div className="wbp-progress-text">{progress}%</div>
        </div>
      )}

      <div className="wbp-canvas-wrapper">
        <canvas ref={canvasRef} className="wbp-canvas" />
        <canvas ref={handCanvasRef} className="wbp-hand-canvas" />
        <canvas ref={finalCanvasRef} style={{ display: "none" }} />
      </div>
    </div>
  );
}
